import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { includes } from '../../utils/array';

type InstructionKind = (typeof INSTRUCTION_KINDS)[number];

type UseInstructionsVisibility = {
  instructionsVisibility: Record<InstructionKind, boolean>;
  toggleInstructionsVisibility: (kind: InstructionKind) => void;
  resetInstructionsVisibility: () => void;
};

const INSTRUCTION_KINDS = ['createCustomArea', 'editCustomArea'] as const;

const INSTRUCTIONS_VISIBILITY_KEY = 'geotra.instructions_visibility';

const DEFAULT_VISIBILITY = {
  createCustomArea: true,
  editCustomArea: true,
} as const satisfies Record<InstructionKind, boolean>;

const parseInstructionsVisibility = (
  json: string | null
): Record<InstructionKind, boolean> => {
  try {
    const object = JSON.parse(json || '{}');
    if (!object || typeof object !== 'object') {
      return DEFAULT_VISIBILITY;
    }
    return {
      ...DEFAULT_VISIBILITY,
      ...Object.fromEntries(
        Object.entries(object).filter(
          (item: [string, unknown]): item is [InstructionKind, boolean] => {
            return (
              includes(INSTRUCTION_KINDS, item[0]) &&
              typeof item[1] === 'boolean'
            );
          }
        )
      ),
    };
  } catch {
    return DEFAULT_VISIBILITY;
  }
};

const instructionsVisibilityAtom = atom<Record<InstructionKind, boolean>>(
  parseInstructionsVisibility(
    window.localStorage.getItem(INSTRUCTIONS_VISIBILITY_KEY)
  )
);

const useInstructionsVisibility = (): UseInstructionsVisibility => {
  const [instructionsVisibility, setInstructionsVisibility] = useAtom(
    instructionsVisibilityAtom
  );

  const toggleInstructionsVisibility = useCallback(
    (kind: InstructionKind): void => {
      const newVisibility = {
        ...instructionsVisibility,
        [kind]: !instructionsVisibility[kind],
      };
      setInstructionsVisibility(newVisibility);
      window.localStorage.setItem(
        INSTRUCTIONS_VISIBILITY_KEY,
        JSON.stringify(newVisibility)
      );
    },
    [instructionsVisibility, setInstructionsVisibility]
  );

  const resetInstructionsVisibility = useCallback((): void => {
    setInstructionsVisibility(DEFAULT_VISIBILITY);
    window.localStorage.setItem(
      INSTRUCTIONS_VISIBILITY_KEY,
      JSON.stringify(DEFAULT_VISIBILITY)
    );
  }, [setInstructionsVisibility]);

  return {
    instructionsVisibility,
    toggleInstructionsVisibility,
    resetInstructionsVisibility,
  };
};

export { useInstructionsVisibility };
